import { filterOutGroupModality } from '@uplift/utils';
import { SelectedProviderFieldTransitionConfig, SelectedProviderSectionProps, SelectedProviderType } from './types';
import { GetProviderPublicProfileResponse, ProviderMatchingProfileBookmark, State } from '@uplift/api';
import uniqBy from 'lodash/uniqBy';
import { ProviderMedicalRole } from '@uplift/types';

const defaultState = { id: '', shortName: '', fullName: '' };

export const convertPublicProfileToSelectedProviderType = (publicProfile: GetProviderPublicProfileResponse['data'], states: State[]): SelectedProviderType => {
  const {
    user,
    profile,
    avatarAsset,
    practice,
    locations,
    licenses,
    specialties,
    modalities,
    supportedAgeGroups,
    treatmentApproaches,
    ethnicities,
    faiths,
    qualification,
    communityExpertise,
    languages,
    therapeuticStyles,
    genders,
    nameDisplay,
    npi,
    supportedInsuranceCompanies,
  } = publicProfile;

  return {
    id: user.id,
    createdAt: user.createdAt,
    firstName: user.firstName,
    lastName: user.lastName,
    nameDisplay: nameDisplay,
    medicalRole: user.medicalRole ?? ProviderMedicalRole.therapist,
    userProfile: {
      preferredName: profile.preferredName,
      pronouns: profile.pronouns ?? '',
      shortBio: profile.shortBio ?? '',
      mentalRoles: uniqBy(licenses, 'role.id').map(x => x.role),
      license: licenses
        .filter(x => x.live)
        .map(x => ({
          id: x.id,
          name: x.role.name,
          state: states.find(s => s.id === x.stateId) ?? defaultState,
          expiration: x.expiration,
          number: x.number
        })),
      avatarAsset: avatarAsset?.url ? { url: avatarAsset.url } : { url: '' },

      practice: {
        practiceName: practice.practiceName,
        practiceServiceType: practice.practiceServiceType,
        locations: locations
          .map(x => ({
            id: x.id,
            addressLine1: x.address.line1 ?? '',
            addressLine2: x.address.line2 ?? '',
            city: x.address.city,
            zip: x.address.zip,
            state: states.find(s => s.id === x.address.stateId) ?? defaultState,
            hideStreetAddress: x.streetAddressHidden
          }))
      }
    },
    specialties: specialties.map(x => ({ id: x.specialtyId, name: x.name })),
    supportedAgeGroups,
    modalities: modalities.filter(filterOutGroupModality),
    treatmentApproach: treatmentApproaches,
    ethnicity: ethnicities.map(e => ({ id: e.ethnicityId, name: e.name })),
    faith: faiths.map(f => ({ id: f.faithId, name: f.name })),
    qualification: qualification,
    communityExpertise: {
      ethnicity: communityExpertise.ethnicities.map(e => ({ id: e.ethnicityId, name: e.name })),
      faith: communityExpertise.faiths.map(f => ({ id: f.faithId, name: f.name })),
      general: communityExpertise.general.map(g => ({ id: g.expertiseId, name: g.name })),
    },
    languages: languages,
    therapeuticStyles: therapeuticStyles.map(x => ({ id: x.therapeuticStyleId, name: x.name, description: x.description })),
    genders: genders.map(x => ({ id: x.genderId, name: x.name })),
    npi: npi,
    supportedInsuranceCompanies: supportedInsuranceCompanies
  };
};

const fieldTransitionConfigDefault: SelectedProviderFieldTransitionConfig = {
  shown: true,
  transitionIn: true,
  active: false,
};


export const useSelectedProviderFieldTransitionConfigs = (configs?: SelectedProviderSectionProps['fieldTransitions']) => {
  const getTransitionConfig = (id: ProviderMatchingProfileBookmark) => {
    return configs?.[id] || fieldTransitionConfigDefault;
  };

  return { getTransitionConfig };
};
